/**
 * Time on item (STRATEGY.md 2026-10-02, pillar 3, gap 5).
 *
 * How long a finding was on screen before its verdict. It is what makes review load something we
 * can feel rather than only count: two studies with the same number of decisions can cost a doctor
 * very different afternoons.
 *
 * The clock runs only while the tab is visible, and stops counting after `IDLE_MS` without input,
 * so a reviewer who walks away does not record a twenty-minute look at one beat. The server caps
 * whatever arrives at thirty minutes as well.
 */

import { useCallback, useEffect, useRef } from 'react';

const IDLE_MS = 2 * 60 * 1000;

interface Clock {
  accumulated: number;
  runningSince: number | null;
  lastInput: number;
}

const now = () => performance.now();

function settle(c: Clock, at: number) {
  if (c.runningSince === null) return;
  // Time after the last input plus the idle allowance is not review time.
  const end = Math.min(at, c.lastInput + IDLE_MS);
  if (end > c.runningSince) c.accumulated += end - c.runningSince;
  c.runningSince = null;
}

/**
 * Restarts whenever `key` changes (a different finding focused, or the add form opened). `read()`
 * returns the milliseconds counted so far for the current key.
 */
export function useDwellClock(key: string | null) {
  const clock = useRef<Clock>({ accumulated: 0, runningSince: now(), lastInput: now() });

  useEffect(() => {
    const t = now();
    clock.current = {
      accumulated: 0,
      runningSince: document.visibilityState === 'visible' ? t : null,
      lastInput: t,
    };
  }, [key]);

  useEffect(() => {
    const onVisibility = () => {
      const t = now();
      if (document.visibilityState === 'visible') {
        clock.current.runningSince = t;
        clock.current.lastInput = t;
      } else {
        settle(clock.current, t);
      }
    };
    const onInput = () => {
      const t = now();
      const c = clock.current;
      // Back from idle: what was idle is already excluded by `settle`; start a fresh stretch.
      if (c.runningSince !== null && t - c.lastInput > IDLE_MS) {
        settle(c, t);
        c.runningSince = t;
      } else if (c.runningSince === null && document.visibilityState === 'visible') {
        c.runningSince = t;
      }
      c.lastInput = t;
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('keydown', onInput, true);
    window.addEventListener('pointerdown', onInput, true);
    window.addEventListener('wheel', onInput, { capture: true, passive: true });
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('keydown', onInput, true);
      window.removeEventListener('pointerdown', onInput, true);
      window.removeEventListener('wheel', onInput, true);
    };
  }, []);

  return useCallback(() => {
    const c = clock.current;
    const t = now();
    let total = c.accumulated;
    if (c.runningSince !== null) total += Math.max(0, Math.min(t, c.lastInput + IDLE_MS) - c.runningSince);
    return Math.round(total);
  }, []);
}
